import { defineComponent as _defineComponent } from 'vue'
import { ref, onMounted, computed } from 'vue';
import { toString, ICurrentUser } from '@samc/common';
import type { ICellRendererParams } from 'ag-grid-community';
import { Returnable, GridOptions, IIconButtonRenderer } from '@samc/common-ui';
import { TaskTemplateSource } from '@/Sources';
import WorkflowTemplateForm from './WorkflowTemplateForm.vue';
import { useUser } from '@/useUser';
import Entitlements from '@/Entitlements';
import { getContextMenuItemsWithoutExports } from '@/utils';


export default /*#__PURE__*/_defineComponent({
  __name: 'WorkflowTemplatesPage',
  props: {
    verificationToken: { type: String, required: true },
    apiRoot: { type: String, required: true }
  },
  setup(__props: any) {

const props = __props as {
  verificationToken: string,
  apiRoot: string
};



const templates = ref<any[]>([]);
const selectedTemplate = ref<any>({});
const showTemplateModal = ref(false);
const grid = ref<HTMLSamcGridElement | null>(null);
const templateSource = ref(new TaskTemplateSource());

const { user } = useUser();
const entitledToCreateTaskTemplates = computed(() => user.value?.hasEntitlement(Entitlements.TaskManagementModule.CreateTaskTemplates) ?? false);
const entitledToEditOrRemoveTaskTemplates = computed(() => user.value?.hasEntitlement(Entitlements.TaskManagementModule.EditOrRemoveTaskTemplates) ?? false);

const loadTemplates = async () => {
  await templateSource.value.read();
  templates.value = templateSource.value.items;
  if (grid.value) {
    grid.value.rowData = templates.value;
  }
};

const overrideGridOptions = (evt: CustomEvent<Returnable<Partial<GridOptions>>>) => {
  evt.detail.returns({
    columnDefs: [
      {
        headerName: '',
        sortable: false,
        pinned: 'left',
        width: 40,
        suppressHeaderContextMenu: true,
        suppressHeaderMenuButton: true,
        floatingFilter: false,
        cellRenderer: 'iconButtonRenderer',
        cellRendererParams: {
          icon: 'icon-edit',
          onClick: (p: IIconButtonRenderer) => {
            handleEditTemplate((p as unknown as ICellRendererParams).data);
          }
        }
      },
      {
        field: 'templateName',
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        headerName: 'Template Name',
        flex: 2
      },
      {
        field: 'description',
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        headerName: 'Description',
        flex: 3
      },
      {
        field: 'isDeal',
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        headerName: 'Acquisition / Sale',
        width: 150,
        valueGetter: (params: any) => {
          return params?.data.isDeal ? "Sale" : "Acquisition";
        }
      },
      {
        field: 'createdDate',
        filter: 'agDateColumnFilter',
        floatingFilter: true,
        headerName: 'Created Date',
        width: 150,
        valueFormatter: (params: any) => {
          if (!params.value) return '';
          return toString(new Date(params.value), 'MM/dd/yyyy');
        }
      },
      {
        field: 'createdBy',
        filter: 'agTextColumnFilter',
        headerName: 'Created By',
        width: 120
      },
      {
        field: 'modifiedDate',
        filter: 'agDateColumnFilter',
        floatingFilter: true,
        headerName: 'Modified Date',
        width: 150,
        valueFormatter: (params: any) => {
          if (!params.value) return '';
          return toString(new Date(params.value), 'MM/dd/yyyy');
        }
      },
      {
        field: 'modifiedBy',
        filter: 'agTextColumnFilter',
        floatingFilter: true,
        headerName: 'Modified By',
        width: 120
      }
    ],
    getContextMenuItems: getContextMenuItemsWithoutExports
  });
};

onMounted(async () => {
  await loadTemplates();
});

const handleCreateTemplate = () => {
  selectedTemplate.value = {};
  showTemplateModal.value = true;
};

const handleEditTemplate = async (template: any) => {
  const id = template?.id;
  if (id === undefined || id === null) {
    return;
  }
  window.loading?.('Loading template...');
  try {
    const full = await templateSource.value.getById(Number(id));
    selectedTemplate.value = full;
    showTemplateModal.value = true;
  } catch (error) {
    console.error('Failed to load workflow template:', error);
    window.showAlert?.error('Failed to load template');
  } finally {
    window.loading?.();
  }
};

const onTemplateSaved = async () => {
  showTemplateModal.value = false;
  selectedTemplate.value = {};
  await loadTemplates();
};

const onTemplateCancelled = () => {
  showTemplateModal.value = false;
  selectedTemplate.value = {};
};

return { __sfc: true,props, templates, selectedTemplate, showTemplateModal, grid, templateSource, user, entitledToCreateTaskTemplates, entitledToEditOrRemoveTaskTemplates, loadTemplates, overrideGridOptions, handleCreateTemplate, handleEditTemplate, onTemplateSaved, onTemplateCancelled, WorkflowTemplateForm }
}

})